import React from "react";
import { Bot, Mic, Globe2, Target, Sparkles, Zap } from "lucide-react";
import { useI18n } from "../i18n";

/**
 * GoldoraStrengths — concise list of GOLDORA's 6 strongest selling points.
 * Translated into all 8 supported languages (pt, en, es, zh, de, fr, ja, hi).
 * No competitor names are mentioned — legal-safe and brand-positive.
 */
const ICONS = [Bot, Mic, Globe2, Target, Sparkles, Zap];

const STRENGTHS = {
  pt: {
    label: "Diferenciais GOLDORA",
    items: [
      ["IA conversacional de última geração", "Claude Sonnet 4.5 com retrieval sobre dados reais — o modelo de raciocínio mais avançado do mercado."],
      ["Pergunte falando, em 8 idiomas", "Português, inglês, espanhol, francês, alemão, italiano, japonês e mandarim. Resposta em texto e voz."],
      ["Mercado global cross-asset em tempo real", "17 moedas · 12 índices mundiais · top 20 criptos · imóveis · commodities. Tudo num só lugar."],
      ["Coaching estratégico gamificado", "Diagnóstico 0–100, streaks diários, trilhas educacionais. Mantém você no jogo, não só informado."],
      ["Decisão da Semana curada", "Toda segunda-feira: análise pronta com tese, cenários e veredito claro. Sem ruído."],
      ["Comparador visual de 6 ativos", "Imóvel Miami vs Bitcoin vs S&P 500 — lado a lado, em segundos, com IA explicando o porquê."],
    ],
    cta_strong: "E ainda tem muito mais por dentro.",
    cta_text: "Continue cadastrando sua conta para descobrir tudo o que a GOLDORA pode fazer por você.",
  },
  en: {
    label: "GOLDORA differentiators",
    items: [
      ["Frontier-grade conversational AI", "Claude Sonnet 4.5 with retrieval over real data — the most advanced reasoning model on the market."],
      ["Ask out loud, in 8 languages", "Portuguese, English, Spanish, French, German, Italian, Japanese and Mandarin. Reply in text and voice."],
      ["Live global cross-asset market", "17 currencies · 12 world indices · top 20 cryptos · real estate · commodities. All in one place."],
      ["Gamified strategic coaching", "0–100 diagnostic, daily streaks, educational tracks. Keeps you in the game, not just informed."],
      ["Curated Decision of the Week", "Every Monday: ready-made analysis with thesis, scenarios and clear verdict. No noise."],
      ["Visual comparator across 6 assets", "Miami real estate vs Bitcoin vs S&P 500 — side by side in seconds, with AI explaining why."],
    ],
    cta_strong: "And there's a lot more inside.",
    cta_text: "Continue creating your account to discover everything GOLDORA can do for you.",
  },
  es: {
    label: "Diferenciales GOLDORA",
    items: [
      ["IA conversacional de última generación", "Claude Sonnet 4.5 con recuperación sobre datos reales — el modelo de razonamiento más avanzado del mercado."],
      ["Pregunta por voz, en 8 idiomas", "Portugués, inglés, español, francés, alemán, italiano, japonés y mandarín. Respuesta en texto y voz."],
      ["Mercado global cross-asset en tiempo real", "17 divisas · 12 índices mundiales · top 20 criptos · inmuebles · commodities. Todo en un solo lugar."],
      ["Coaching estratégico gamificado", "Diagnóstico 0–100, rachas diarias, rutas educativas. Te mantiene en el juego, no solo informado."],
      ["Decisión de la Semana curada", "Cada lunes: análisis listo con tesis, escenarios y veredicto claro. Sin ruido."],
      ["Comparador visual de 6 activos", "Inmueble Miami vs Bitcoin vs S&P 500 — lado a lado, en segundos, con IA explicando el porqué."],
    ],
    cta_strong: "Y hay mucho más por dentro.",
    cta_text: "Continúa creando tu cuenta para descubrir todo lo que GOLDORA puede hacer por ti.",
  },
  zh: {
    label: "GOLDORA 核心优势",
    items: [
      ["前沿级对话式 AI", "Claude Sonnet 4.5 结合真实数据检索 — 市面上最先进的推理模型。"],
      ["8 种语言语音提问", "葡萄牙语、英语、西班牙语、法语、德语、意大利语、日语和普通话。文本与语音回复。"],
      ["跨资产全球市场实时数据", "17 种货币 · 12 个全球指数 · 前 20 加密币 · 房产 · 大宗商品。一站式覆盖。"],
      ["游戏化战略教练", "0–100 诊断、每日连胜、教育课程。让你保持参与,而非仅仅获取信息。"],
      ["每周精选决策", "每周一推送:论点、情景与明确结论的成品分析。零噪音。"],
      ["6 资产可视化对比", "迈阿密房产 vs 比特币 vs 标普 500 — 数秒并排比较,AI 解释原因。"],
    ],
    cta_strong: "里面还有更多精彩内容。",
    cta_text: "请继续完成注册,探索 GOLDORA 能为你提供的一切。",
  },
  de: {
    label: "GOLDORA Alleinstellungsmerkmale",
    items: [
      ["Hochmoderne dialogfähige KI", "Claude Sonnet 4.5 mit Retrieval über echte Daten — das fortschrittlichste Reasoning-Modell auf dem Markt."],
      ["Sprachfragen in 8 Sprachen", "Portugiesisch, Englisch, Spanisch, Französisch, Deutsch, Italienisch, Japanisch und Mandarin. Antwort als Text und Sprache."],
      ["Globaler Cross-Asset-Markt in Echtzeit", "17 Währungen · 12 Weltindizes · Top 20 Kryptos · Immobilien · Rohstoffe. Alles an einem Ort."],
      ["Gamifiziertes strategisches Coaching", "0–100 Diagnose, tägliche Streaks, Lernpfade. Hält Sie im Spiel, nicht nur informiert."],
      ["Kuratierte Entscheidung der Woche", "Jeden Montag: fertige Analyse mit These, Szenarien und klarer Empfehlung. Ohne Rauschen."],
      ["Visueller Vergleich von 6 Anlagen", "Immobilie Miami vs Bitcoin vs S&P 500 — nebeneinander, in Sekunden, mit KI-Erklärung."],
    ],
    cta_strong: "Und es gibt noch viel mehr im Inneren.",
    cta_text: "Vervollständigen Sie Ihr Konto, um alles zu entdecken, was GOLDORA für Sie tun kann.",
  },
  fr: {
    label: "Atouts GOLDORA",
    items: [
      ["IA conversationnelle de dernière génération", "Claude Sonnet 4.5 avec récupération sur données réelles — le modèle de raisonnement le plus avancé du marché."],
      ["Posez vos questions à la voix, en 8 langues", "Portugais, anglais, espagnol, français, allemand, italien, japonais et mandarin. Réponse en texte et voix."],
      ["Marché global cross-asset en temps réel", "17 devises · 12 indices mondiaux · top 20 cryptos · immobilier · matières premières. Tout au même endroit."],
      ["Coaching stratégique gamifié", "Diagnostic 0–100, séries quotidiennes, parcours éducatifs. Vous garde dans le jeu, pas seulement informé."],
      ["Décision de la Semaine sélectionnée", "Chaque lundi : analyse prête avec thèse, scénarios et verdict clair. Sans bruit."],
      ["Comparateur visuel de 6 actifs", "Immobilier Miami vs Bitcoin vs S&P 500 — côte à côte, en secondes, avec l'IA qui explique."],
    ],
    cta_strong: "Et il y a beaucoup plus à l'intérieur.",
    cta_text: "Continuez à créer votre compte pour découvrir tout ce que GOLDORA peut faire pour vous.",
  },
  ja: {
    label: "GOLDORAの強み",
    items: [
      ["最先端の対話型AI", "Claude Sonnet 4.5と実データ検索の組み合わせ — 市場で最も高度な推論モデル。"],
      ["8言語で音声質問", "ポルトガル語、英語、スペイン語、フランス語、ドイツ語、イタリア語、日本語、北京語。テキストと音声で返答。"],
      ["クロスアセット・グローバル市場をリアルタイムで", "17通貨・12世界指数・上位20仮想通貨・不動産・コモディティ。すべて一箇所に。"],
      ["ゲーミフィケーション戦略コーチング", "0–100診断、毎日の連続記録、教育トラック。情報を得るだけでなく、ゲームに留まらせる。"],
      ["毎週厳選された決定", "毎週月曜:論点、シナリオ、明確な結論を含む完成済み分析。ノイズなし。"],
      ["6資産のビジュアル比較", "マイアミ不動産 vs ビットコイン vs S&P 500 — 数秒で並列比較、AIが理由を解説。"],
    ],
    cta_strong: "中にはもっと多くのものがあります。",
    cta_text: "アカウント作成を続けて、GOLDORAができるすべてを発見しましょう。"
  },
  hi: {
    label: "GOLDORA की विशेषताएं",
    items: [
      ["अत्याधुनिक संवादी AI", "वास्तविक डेटा पर रिट्रीवल के साथ Claude Sonnet 4.5 — बाजार का सबसे उन्नत तर्क मॉडल।"],
      ["8 भाषाओं में आवाज़ से पूछें", "पुर्तगाली, अंग्रेज़ी, स्पेनिश, फ्रेंच, जर्मन, इतालवी, जापानी और मंदारिन। पाठ और आवाज़ में उत्तर।"],
      ["रीयल-टाइम वैश्विक क्रॉस-एसेट बाजार", "17 मुद्राएं · 12 विश्व सूचकांक · शीर्ष 20 क्रिप्टो · रियल एस्टेट · कमोडिटीज। सब एक जगह।"],
      ["गेमीफाइड रणनीतिक कोचिंग", "0–100 निदान, दैनिक स्ट्रीक, शैक्षिक ट्रैक। आपको खेल में बनाए रखता है, सिर्फ जानकारी नहीं देता।"],
      ["सप्ताह का चयनित निर्णय", "हर सोमवार: थीसिस, परिदृश्य और स्पष्ट निर्णय के साथ तैयार विश्लेषण। बिना शोर।"],
      ["6 संपत्तियों का दृश्य तुलनात्मक", "मियामी रियल एस्टेट बनाम बिटकॉइन बनाम S&P 500 — साथ-साथ, सेकंडों में, AI कारण समझाते हुए।"],
    ],
    cta_strong: "और भी बहुत कुछ अंदर है।",
    cta_text: "GOLDORA आपके लिए क्या कर सकता है यह जानने के लिए अपना खाता बनाना जारी रखें।",
  },
};

export default function GoldoraStrengths({ compact = false }) {
  const { lang } = useI18n();
  const data = STRENGTHS[lang] || STRENGTHS.en;

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="goldora-strengths">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
        ◆ {data.label}
      </div>

      <ul className={`grid gap-${compact ? "2.5" : "3"}`}>
        {data.items.map(([title, body], i) => {
          const Icon = ICONS[i] || Sparkles;
          return (
            <li
              key={i}
              className="flex gap-3 items-start"
              data-testid={`strength-${i}`}
            >
              <span className="shrink-0 w-8 h-8 rounded-md grid place-items-center bg-gold-500/10 border border-gold-500/30 text-gold-400">
                <Icon size={15} strokeWidth={2.2} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm text-white leading-tight">{title}</div>
                <div className={`text-[12px] text-neutral-400 leading-snug ${compact ? "mt-0.5" : "mt-1"}`}>
                  {body}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 pt-4 border-t border-gold-500/15 flex items-start gap-2.5" data-testid="strengths-cta">
        <span className="text-gold-400 mt-0.5">✨</span>
        <p className="text-[12px] text-neutral-300 leading-relaxed">
          <strong className="text-gold-200">{data.cta_strong}</strong>{" "}{data.cta_text}
        </p>
      </div>
    </div>
  );
}
